/*
 * @Author: Ryan
 * @Date: 2026-01-16
 * @Description: SEO Head 组件 - 封装所有 SEO 相关的 meta 标签
 */
import Head from 'next/head';
import React from 'react';

export interface SEOHeadProps {
  /** 页面标题 */
  title: string;
  /** 页面描述 */
  description: string;
  /** 页面 URL */
  url: string;
  /** SEO 关键词（逗号分隔） */
  keywords?: string;
  /** OG 图片 URL */
  image?: string;
  /** OG 图片 alt 文本 */
  imageAlt?: string;
  /** 站点名称 */
  siteName?: string;
  /** Twitter 账号 */
  twitterHandle?: string;
  /** 发布日期 (YYYY-MM-DD) */
  publishedTime: string;
  /** 修改日期 (YYYY-MM-DD) */
  modifiedTime: string;
  /** 结构化数据 (JSON-LD)，支持单个或多个 */
  structuredData?: object | object[];
  /** 预加载图片列表 */
  preloadImages?: string[];
  /** 是否添加 canonical 链接，默认 true */
  canonical?: boolean;
  /** 预连接域名列表 */
  preconnect?: string[];
}

/** 默认 OG 图片 */
const DEFAULT_OG_IMAGE = 'https://falcocut.ai/images/og-img.png';
/** 默认站点名称 */
const DEFAULT_SITE_NAME = 'falcocut.ai';
/** 默认 Twitter 账号 */
const DEFAULT_TWITTER_HANDLE = '@falcocut';
/** OG 图片宽度 */
const OG_IMAGE_WIDTH = '1200';
/** OG 图片高度 */
const OG_IMAGE_HEIGHT = '630';

/**
 * 格式化日期为 ISO 格式
 * @param date - 日期字符串 (YYYY-MM-DD)
 * @returns ISO 格式日期字符串
 */
const formatDateToISO = (date: string): string => {
  return `${date}T00:00:00+00:00`;
};

/** 默认预连接域名 */
const DEFAULT_PRECONNECT = ['https://assets.falcocut.ai', 'https://fonts.googleapis.com'];

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  url,
  keywords,
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  siteName = DEFAULT_SITE_NAME,
  twitterHandle = DEFAULT_TWITTER_HANDLE,
  publishedTime,
  modifiedTime,
  structuredData,
  preloadImages = [],
  canonical = true,
  preconnect = DEFAULT_PRECONNECT,
}) => {
  const ogImageAlt = imageAlt || title;

  return (
    <Head>
      {/* 基础 SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={url} />}

      {/* Robots - 可索引性 */}
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      {/* Hreflang - 多语言支持 */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content={OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* 发布日期和修改日期 - AEO */}
      <meta property="article:published_time" content={formatDateToISO(publishedTime)} />
      <meta property="article:modified_time" content={formatDateToISO(modifiedTime)} />

      {/* 结构化数据 - JSON-LD（支持多个 Schema） */}
      {structuredData &&
        (Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script
              key={index}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
            />
          ))
        ) : (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        ))}

      {/* 预连接 - 提前建立连接 */}
      {preconnect.map((href, index) => (
        <link
          key={`preconnect-${index}`}
          rel="preconnect"
          href={href}
          crossOrigin="anonymous"
        />
      ))}

      {/* DNS 预解析 */}
      {preconnect.map((href, index) => (
        <link key={`dns-${index}`} rel="dns-prefetch" href={href} />
      ))}

      {/* 预加载图片 */}
      {preloadImages.map((src, index) => (
        <link
          key={`preload-${index}`}
          rel="preload"
          href={src}
          as="image"
          fetchPriority="high"
        />
      ))}
    </Head>
  );
};

export default SEOHead;
